/**
 * 调用日志（S5b）的文案。命名空间 `visualGameAdmin.llm.calls.*`。
 *
 * **这里没有一句说明性文字。** 页面上的每一段话都短到能一眼读完；原来印在页面上的那些「为什么」
 * （key 挂在厂商下面、request_id 不叠时间窗、失败调用为什么不计费、链是怎么降级的）要么变成了控件的
 * 状态（disabled、徽标、颜色），要么进了 `plan/llm-admin/`，不再印给运营看。
 *
 * 钱一律是人民币（`call-format.js` 的 `formatYuan`）：这里的文案只给标签，不给单位，金额一律 ¥ 开头。
 *
 * `en` 与 `zh` 的 key 集必须逐字相同 —— `test/i18n.test.js` 拍平后逐项比对六个语种。
 */

export const en = {
  title: 'Call log',
  capability: {
    text: 'Text generation',
    image: 'Image generation',
    video: 'Video generation',
    tts: 'Speech synthesis',
    embedding: 'Embedding',
  },
  filters: {
    capability: 'Model capability',
    window: 'Window',
    windows: {
      '1h': 'Last hour',
      '24h': 'Last 24 hours',
      '7d': 'Last 7 days',
      '30d': 'Last 30 days',
      custom: 'Custom range',
    },
    since: 'From',
    until: 'To',
    model: 'Model',
    status: 'Status',
    user: 'User',
    session: 'Session',
    requestId: 'Request ID',
    providerRequestId: 'Provider request ID',
    key: 'Key',
    billingMode: 'Billing mode',
    costState: 'Cost state',
    all: 'All',
    apply: 'Search',
    reset: 'Reset',
    more: 'More filters',
    less: 'Less filters',
    // 三个自由输入框都是精确匹配（后端拿它们做等值查询），不是模糊搜索 —— 说在占位文案里。
    placeholder: {
      user: 'Exact user id',
      session: 'Exact session id',
      requestId: 'Exact request id',
      providerRequestId: 'Exact provider request id',
    },
  },
  table: {
    time: 'Time',
    provider: 'Provider capability',
    model: 'Model',
    capability: 'Capability / model',
    status: 'Status',
    tokens: 'Tokens',
    usage: 'Usage',
    cost: 'Supplier cost',
    billing: 'Billing / points',
    accounting: 'Cost / billing',
    duration: 'Duration',
    user: 'User',
    attribution: 'Duration / user',
    route: 'Route',
    key: 'Key',
    open: 'Open',
    preview: 'Preview',
  },
  status: {
    ok: 'ok',
    error: 'error',
    aborted: 'aborted',
    failed: 'failed',
  },
  billingMode: {
    record_only: 'Record only',
    fixed_points: 'Fixed points',
    cost_ratio: 'Cost ratio',
    legacy_record_only: 'Legacy record only',
    legacy_points: 'Legacy points',
  },
  costState: {
    all: 'All',
    priced: 'Priced',
    unpriced: 'Unpriced',
    charged: 'Charged',
  },
  usage: {
    input: 'input',
    output: 'output',
    cached: 'cached',
    cacheWrite: 'cache write',
    images: 'images',
    characters: 'characters',
    dimensions: 'dims',
    with_video_input: 'with video input',
    without_video_input: 'without video input',
    videoCostFormula: '{tokens} tokens × {rate} / M tokens',
  },
  route: {
    routed: '{mode}',
    notRouted: 'No chain',
    broken: 'Broken attribution',
  },
  key: {
    deleted: 'Key deleted (id {id})',
    // `llm_call_log.key_id IS NULL`：还没租到 key 就失败了 —— 池子里没有可用 key 或配置无效，请求没发出。
    // 界面上不出现 `keyrouter` 这种模块名 —— 运营认的是「密钥池」，不是我们的内部实现。
    noKeyLeased: 'Failed before a key was leased',
    // 成功却没有 key：今天的网关写不出这种行。是脏数据，标红。
    broken: 'Succeeded with no key — dirty row',
    noLabel: '(no label)',
  },
  empty: {
    title: 'No call in this window',
    action: 'Widen to the last 7 days',
  },
  detail: {
    title: 'Call {id}',
    createdAt: 'Created at',
    identifiers: 'Identifiers',
    callId: 'Call ID',
    providerRequestId: 'Provider request ID',
    providerTaskId: 'Provider task ID',
    ledgerId: 'Ledger ID',
    preview: 'Preview',
    request: 'Request',
    response: 'Response',
    video: {
      request: { eyebrow: 'Submitted to provider', title: 'Generation request' },
      response: { eyebrow: 'Returned by provider', title: 'Generation result' },
      model: 'Model',
      prompt: 'Prompt',
      imageInput: 'Image input',
      resolution: 'Resolution',
      ratio: 'Aspect ratio',
      duration: 'Duration',
      generateAudio: 'Generate audio',
      returnLastFrame: 'Return last frame',
      watermark: 'Watermark',
      enabled: 'Enabled',
      disabled: 'Disabled',
      taskStatus: 'Task status',
      seed: 'Seed',
      framesPerSecond: 'Frames per second',
      serviceTier: 'Service tier',
      outputTokens: 'Output tokens',
      totalTokens: 'Total tokens',
      inputMedia: { eyebrow: 'Visual context', title: 'Input media' },
      firstFrame: 'Game first frame',
      referenceImage: 'Game reference image',
      referenceVideo: 'Previous segment video',
      gameImage: 'Game asset',
      continuityVideo: 'Previous generated segment',
      associatedGameAsset: 'Linked game reference',
      openImage: 'Open image',
      openVideo: 'Open video',
      providerAssetId: 'Provider asset ID',
      referenceRole: {
        npc: 'NPC identity reference',
        player: 'Player identity reference',
      },
      storedEndFrame: 'Stored end frame',
      storedOutputUnavailable: {
        title: 'No stored preview is available',
        description: 'This call has no locally stored video or end frame. Provider delivery URLs are retained in the raw response only and are not used as previews.',
      },
      errorCode: 'Error code',
      errorType: 'Error type',
      errorParameter: 'Error parameter',
      errorMessage: 'Error message',
      noResponse: 'The provider has not returned a response.',
      rawRequest: 'Raw request JSON',
      rawResponse: 'Raw response JSON',
    },
    error: {
      title: 'Failure details',
      category: 'Category',
      message: 'Message',
      detail: 'Provider response',
    },
    accounting: 'Cost and billing',
    providerFields: 'Provider fields',
    metrics: {
      title: 'Metrics',
      time: 'Started at',
      provider: 'Provider capability',
      model: 'Model',
      errorReason: 'Error',
      duration: 'Duration',
      ttft: 'TTFT',
      prompt_tokens: 'Input tokens',
      cached_tokens: 'Cached input',
      cache_write_tokens: 'Cache write input',
      completion_tokens: 'Output tokens',
      user: 'User',
      session: 'Session',
      requestId: 'Request ID',
    },
    content: {
      title: 'Conversation',
      reasoning: 'Reasoning',
    },
    billing: {
      title: 'Billing',
      state: {
        charged: 'Charged',
        not_charged_failed_call: 'Not charged',
        missing_ledger_row: 'Not charged',
        ledger_mismatch: 'Ledger mismatch',
        settlement_mismatch: 'Settlement mismatch',
        settlement_pending: 'Settlement pending',
        record_only: 'Cost recorded only',
        not_charged_zero_points: 'Zero points',
        unpriceable: 'Cost pending',
      },
      charged: 'Charged',
      balanceAfter: 'Balance after',
      ledgerId: 'Ledger row',
      chargedAt: 'Charged at',
      policyMode: 'Billing policy',
      policyDetail: 'Policy parameters',
      mode: {
        record_only: 'Record only',
        fixed_points: 'Fixed points',
        cost_ratio: 'Cost ratio',
        legacy_record_only: 'Legacy record only',
        legacy_points: 'Legacy points',
      },
      policy: {
        recordOnly: 'Reason: {reason}',
        fixedPoints: '{points} points per call',
        costRatio: '{points} points per yuan of supplier cost',
      },
      legacyPointsExplanation: 'Historical deduction matched to one exact ledger row; the original policy parameters are unknown.',
      legacyRecordOnlyExplanation: 'Historical record-only result; the original reason parameter was not stored.',
      expected: 'Should have been',
      notChargedFailedCall: 'Not charged — the call failed',
      notChargedZeroCost: 'Not charged — zero-cost model',
      missingLedgerRow: 'Tokens burned, nobody charged',
      gotoRecon: 'Open reconciliation',
      gotoLedger: 'Ledger',
      unpriceable: 'Supplier cost was not recorded',
      unpriceableReason: {
        supplier_cost_not_recorded: 'Supplier cost was not recorded',
      },
    },
    attribution: {
      title: 'Attribution',
      key: 'Key',
      route: 'Routing',
    },
    chain: {
      title: 'Routing chain',
      step: 'Attempt {index}',
      current: 'Current',
      answered: 'OK',
      failed: 'Failed',
      aborted: 'Aborted',
      notRouted: 'Not routed',
    },
  },
  longText: {
    expand: 'Expand',
    collapse: 'Collapse',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Copy failed',
    size: '{chars} characters',
  },
  pagination: {
    prev: 'Previous',
    next: 'Next',
    summary: '{total} calls · page {page} of {pages}',
    pageSize: 'Per page',
  },
}

export const zh = {
  title: '调用日志',
  capability: {
    text: '文本生成',
    image: '图片生成',
    video: '视频生成',
    tts: '语音合成',
    embedding: '向量',
  },
  filters: {
    capability: '模型能力',
    window: '时间窗',
    windows: {
      '1h': '最近 1 小时',
      '24h': '最近 24 小时',
      '7d': '最近 7 天',
      '30d': '最近 30 天',
      custom: '自定义区间',
    },
    since: '从',
    until: '到',
    model: '模型',
    status: '状态',
    user: '用户',
    session: '会话',
    requestId: 'Request ID',
    providerRequestId: '供应商请求 ID',
    key: 'API Key',
    billingMode: '计费方式',
    costState: '成本状态',
    all: '全部',
    apply: '查询',
    reset: '重置',
    more: '更多筛选',
    less: '收起筛选',
    placeholder: {
      user: '精确匹配用户 ID',
      session: '精确匹配会话 ID',
      requestId: '精确匹配 request id',
      providerRequestId: '精确匹配供应商请求 ID',
    },
  },
  table: {
    time: '时间',
    provider: '能力接入',
    model: '模型',
    capability: '模型能力 / 模型',
    status: '状态',
    tokens: 'Token',
    usage: '用量',
    cost: '供应商成本',
    billing: '计费 / 积分',
    accounting: '成本 / 计费',
    duration: '耗时',
    user: '用户',
    attribution: '耗时 / 用户',
    route: '路由',
    key: 'API Key',
    open: '查看',
    preview: '预览',
  },
  status: {
    ok: '成功',
    error: '失败',
    aborted: '中止',
    failed: '失败',
  },
  billingMode: {
    record_only: '仅记录',
    fixed_points: '固定积分',
    cost_ratio: '成本比例',
    legacy_record_only: '历史仅记录',
    legacy_points: '历史积分',
  },
  costState: {
    all: '全部',
    priced: '已计价',
    unpriced: '未计价',
    charged: '已扣积分',
  },
  usage: {
    input: '输入',
    output: '输出',
    cached: '缓存',
    cacheWrite: '缓存写入',
    images: '张图片',
    characters: '字符',
    dimensions: '维',
    with_video_input: '含视频输入',
    without_video_input: '无视频输入',
    videoCostFormula: '{tokens} Token × {rate} / 百万 Token',
  },
  route: {
    routed: '{mode}',
    notRouted: '无路由链',
    broken: '调用信息不完整',
  },
  key: {
    deleted: 'API Key 已删除（ID {id}）',
    noKeyLeased: '未调用服务：没有可用的 API Key',
    broken: '记录异常：调用成功但缺少 API Key 信息',
    noLabel: '（没有 label）',
  },
  empty: {
    title: '这个窗口里没有调用',
    action: '放宽到最近 7 天',
  },
  detail: {
    title: '调用 {id}',
    createdAt: '创建时间',
    identifiers: '标识',
    callId: '调用 ID',
    providerRequestId: '供应商请求 ID',
    providerTaskId: '供应商任务 ID',
    ledgerId: '积分流水 ID',
    preview: '预览',
    request: '请求',
    response: '响应',
    video: {
      request: { eyebrow: '实际提交给供应商', title: '生成请求' },
      response: { eyebrow: '供应商实际返回', title: '生成结果' },
      model: '模型',
      prompt: '提示词',
      imageInput: '图片输入',
      resolution: '分辨率',
      ratio: '画面比例',
      duration: '时长',
      generateAudio: '生成音频',
      returnLastFrame: '返回尾帧',
      watermark: '水印',
      enabled: '开启',
      disabled: '关闭',
      taskStatus: '任务状态',
      seed: '随机种子',
      framesPerSecond: '帧率',
      serviceTier: '服务等级',
      outputTokens: '输出 Token',
      totalTokens: '总 Token',
      inputMedia: { eyebrow: '画面上下文', title: '输入素材' },
      firstFrame: '游戏首帧',
      referenceImage: '游戏参考图',
      referenceVideo: '上一段视频',
      gameImage: '游戏素材',
      continuityVideo: '上一段生成片段',
      associatedGameAsset: '关联的游戏参考图',
      openImage: '打开原图',
      openVideo: '打开视频',
      providerAssetId: '供应商资产 ID',
      referenceRole: {
        npc: 'NPC 身份参考',
        player: '玩家身份参考',
      },
      storedEndFrame: '已存储尾帧',
      storedOutputUnavailable: {
        title: '没有可预览的已存储资源',
        description: '这条调用没有保存到本地的视频或尾帧。供应商下发地址仅保留在原始响应中，不作为预览资源使用。',
      },
      errorCode: '错误码',
      errorType: '错误类型',
      errorParameter: '错误参数',
      errorMessage: '错误消息',
      noResponse: '供应商尚未返回响应。',
      rawRequest: '原始请求 JSON',
      rawResponse: '原始响应 JSON',
    },
    error: {
      title: '失败详情',
      category: '失败分类',
      message: '错误消息',
      detail: '供应商响应',
    },
    accounting: '成本与计费',
    providerFields: '供应商字段',
    metrics: {
      title: '指标',
      time: '开始于',
      provider: '能力接入',
      model: '模型',
      errorReason: '失败原因',
      duration: '耗时',
      ttft: '首 token',
      prompt_tokens: '输入 Token',
      cached_tokens: '其中缓存命中',
      cache_write_tokens: '其中缓存写入',
      completion_tokens: '输出 Token',
      user: '用户',
      session: '会话',
      requestId: 'Request ID',
    },
    content: {
      title: '对话',
      reasoning: '思考过程',
    },
    billing: {
      title: '计费',
      state: {
        charged: '已扣款',
        not_charged_failed_call: '未扣款',
        missing_ledger_row: '未扣款',
        ledger_mismatch: '账本不匹配',
        settlement_mismatch: '结算状态不匹配',
        settlement_pending: '等待结算',
        record_only: '仅记录成本',
        not_charged_zero_points: '零积分',
        unpriceable: '成本待核算',
      },
      charged: '扣款',
      balanceAfter: '扣完余额',
      ledgerId: '流水行',
      chargedAt: '扣款时间',
      policyMode: '计费策略',
      policyDetail: '策略参数',
      mode: {
        record_only: '仅记录',
        fixed_points: '固定积分',
        cost_ratio: '成本比例',
        legacy_record_only: '历史仅记录',
        legacy_points: '历史积分',
      },
      policy: {
        recordOnly: '原因：{reason}',
        fixedPoints: '每次调用 {points} 积分',
        costRatio: '每 1 元供应商成本折算 {points} 积分',
      },
      legacyPointsExplanation: '历史扣分已与唯一账本流水精确匹配；旧系统没有保存原始策略参数。',
      legacyRecordOnlyExplanation: '历史仅记录结果；旧系统没有保存原始原因参数。',
      expected: '本该扣',
      notChargedFailedCall: '未计费 —— 调用失败',
      notChargedZeroCost: '未计费 —— 零成本模型',
      missingLedgerRow: '供应商用量已记录，未生成积分流水',
      gotoRecon: '查看对账',
      gotoLedger: '查看账本',
      unpriceable: '供应商成本未记录',
      unpriceableReason: {
        supplier_cost_not_recorded: '没有记录供应商成本',
      },
    },
    attribution: {
      title: '调用信息',
      key: 'API Key',
      route: '路由',
    },
    chain: {
      title: '路由链',
      step: '尝试 {index}',
      current: '当前',
      answered: '成功',
      failed: '失败',
      aborted: '中止',
      notRouted: '未走路由链',
    },
  },
  longText: {
    expand: '展开',
    collapse: '收起',
    copy: '复制',
    copied: '已复制',
    copyFailed: '复制失败',
    size: '{chars} 个字符',
  },
  pagination: {
    prev: '上一页',
    next: '下一页',
    summary: '共 {total} 条 · 第 {page} / {pages} 页',
    pageSize: '每页',
  },
}
